/* AT2 EXPERIMENT - Cache Utils */

/**
 * Utilitaires pour gérer le cache localStorage des analyses et générations AT2.
 *
 * Le cache permet d'éviter de refaire les appels API si l'utilisateur ferme
 * la modal de comparaison puis retente d'envoyer le même message.
 *
 * Structure du cache:
 * - analysis: Résultat de l'analyse AT2
 * - improvedMessage: Message optimisé généré
 * - timestamp: Date de création (pour l'expiration)
 */

import type { DossierId } from '../../../../../../../resources/dossier/types';
import type { AT2Analysis } from '../mock/at2-mock-api';

// Durée de validité du cache (30 minutes)
const CACHE_EXPIRATION_MS = 30 * 60 * 1000;

// Cache structure pour localStorage
export type AT2Cache = {
    analysis: AT2Analysis | null;
    improvedMessage: string | null;
    timestamp: number;
};

async function hashString(str: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Génère une clé de cache unique basée sur dossierId + message
 * Utilise un hash simple du message pour éviter les clés trop longues
 */
export const getCacheKey = async (dossierId: DossierId, message: string): Promise<string> => {
    const messageHash = await hashString(message);
    return `at2_cache_${dossierId}_${messageHash}`;
};

/**
 * Récupère le cache depuis localStorage
 * Retourne null si le cache n'existe pas ou est expiré
 */
export const getCache = async (dossierId: DossierId, message: string): Promise<AT2Cache | null> => {
    try {
        const cacheKey = await getCacheKey(dossierId, message);
        const cached = localStorage.getItem(cacheKey);
        if (!cached) return null;

        const cache: AT2Cache = JSON.parse(cached);

        // Invalider le cache si expiré
        const isExpired = Date.now() - cache.timestamp > CACHE_EXPIRATION_MS;
        if (isExpired) {
            localStorage.removeItem(cacheKey);
            return null;
        }

        return cache;
    } catch (error) {
        console.error('[AT2 Cache] Error reading cache:', error);
        return null;
    }
};

/**
 * Sauvegarde ou met à jour le cache dans localStorage
 * Fusionne avec le cache existant si présent
 */
export const saveCache = async (
    dossierId: DossierId,
    message: string,
    cache: Partial<AT2Cache>,
): Promise<void> => {
    try {
        const cacheKey = await getCacheKey(dossierId, message);
        const existing = await getCache(dossierId, message);

        const updated: AT2Cache = {
            analysis: cache.analysis ?? existing?.analysis ?? null,
            improvedMessage: cache.improvedMessage ?? existing?.improvedMessage ?? null,
            timestamp: existing?.timestamp ?? Date.now(),
        };

        localStorage.setItem(cacheKey, JSON.stringify(updated));
    } catch (error) {
        console.error('[AT2 Cache] Error saving cache:', error);
    }
};

/**
 * Supprime le cache du localStorage
 * Appelé après l'envoi du message pour nettoyer
 */
export const clearCache = async (dossierId: DossierId, message: string): Promise<void> => {
    try {
        const cacheKey = await getCacheKey(dossierId, message);
        localStorage.removeItem(cacheKey);
    } catch (error) {
        console.error('[AT2 Cache] Error clearing cache:', error);
    }
};
